// Picker de jogos do torneio: filtro por edição (ou "avulsos") + busca + tabela com
// checkbox.
//
// A seleção NÃO vive aqui — vem do pai por `value`. É o que permite acumular jogos de
// edições diferentes: trocar o filtro recarrega as linhas, mas o que já foi escolhido
// continua marcado.
import { useCallback, useEffect, useState } from 'react';
import { Input, Select, Space, Table, Tag, message } from 'antd';
import {
  listEdicoes,
  listPickableGames,
  type EdicaoAdmin,
  type EditionKind,
  type PickableGame,
} from '../../services/admin';
import { domainColor, domainLabel, editionKindOf } from './academy';

const AVULSOS = 'avulsos';

/** `id` sozinho não identifica: passatempo #1 e jogo #1 são coisas diferentes. */
export const gameKey = (g: { kind: string; id: number }): string => `${g.kind}:${g.id}`;

interface Props {
  value: PickableGame[];
  onChange: (games: PickableGame[]) => void;
  /**
   * Restringe a um tipo de edição (ex.: `MIND_TRAINING`): só edições daquele tipo no filtro,
   * sem "avulsos", e as colunas Área/Pontos. Sem a prop o picker é o de sempre.
   */
  edicaoKind?: EditionKind;
}

export const TorneioGamePicker: React.FC<Props> = ({ value, onChange, edicaoKind }) => {
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [scope, setScope] = useState<number | typeof AVULSOS | undefined>(undefined);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<PickableGame[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listEdicoes()
      .then(setEdicoes)
      .catch(() => message.error('Falha ao carregar edições.'));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        await listPickableGames({
          edicaoId: typeof scope === 'number' ? scope : undefined,
          semEdicao: scope === AVULSOS,
          q: q.trim() || undefined,
          edicaoKind,
        }),
      );
    } catch {
      message.error('Falha ao carregar jogos.');
    } finally {
      setLoading(false);
    }
  }, [scope, q, edicaoKind]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedKeys = value.map(gameKey);

  const toggle = (game: PickableGame, checked: boolean): void => {
    if (checked) onChange([...value, game]);
    else onChange(value.filter((g) => gameKey(g) !== gameKey(game)));
  };

  const visibleEdicoes = edicaoKind
    ? edicoes.filter((e) => editionKindOf(e) === edicaoKind)
    : edicoes;

  const academyColumns = edicaoKind
    ? [
        {
          title: 'Área',
          dataIndex: 'cognitiveDomain',
          width: 130,
          render: (v: PickableGame['cognitiveDomain']) =>
            v ? <Tag color={domainColor(v)}>{domainLabel(v)}</Tag> : '—',
        },
        {
          title: 'Pontos',
          dataIndex: 'academyPoints',
          width: 90,
          render: (v: number | null | undefined) => v ?? '—',
        },
      ]
    : [];

  const columns = [
    {
      title: '',
      key: 'sel',
      width: 40,
      render: (_: unknown, g: PickableGame) => (
        <input
          type="checkbox"
          aria-label={`Selecionar ${g.titulo ?? gameKey(g)}`}
          checked={selectedKeys.includes(gameKey(g))}
          onChange={(e) => toggle(g, e.target.checked)}
        />
      ),
    },
    {
      title: 'Título',
      dataIndex: 'titulo',
      ...(edicaoKind ? { width: 260 } : {}),
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Tipo',
      dataIndex: 'kind',
      width: edicaoKind ? 80 : 110,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Edição',
      dataIndex: 'edicaoNome',
      width: 160,
      render: (v: string | null) => v ?? 'avulso',
    },
    ...academyColumns,
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          style={{ width: 220 }}
          placeholder="Filtrar por edição"
          value={scope}
          onChange={(v) => setScope(v)}
        >
          {!edicaoKind && <Select.Option value={AVULSOS}>Sem edição (avulsos)</Select.Option>}
          {visibleEdicoes.map((e) => (
            <Select.Option key={e.id} value={e.id}>
              {e.nome ?? `Edição #${e.id}`}
            </Select.Option>
          ))}
        </Select>
        <Input.Search
          placeholder="Buscar por título"
          allowClear
          style={{ width: 240 }}
          onSearch={(text) => setQ(text)}
        />
      </Space>

      <p style={{ margin: '4px 0', color: '#666' }}>{value.length} jogo(s) selecionado(s)</p>

      <Table
        rowKey={gameKey}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 8 }}
        scroll={edicaoKind ? { x: 760, y: 260 } : { y: 260 }}
      />
    </div>
  );
};

export default TorneioGamePicker;
